import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { organizationsPublicApi, trialRegistrationsApi } from '../../api/facades';
import { trackEvent } from '../../analytics/track';
import { palette } from '../../theme/palette';

type OrganizationOption = {
  id: number;
  name: string;
  slug?: string;
};

const TrialRegisterPage: React.FC = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [organizationInput, setOrganizationInput] = useState('');
  const [selectedOrganization, setSelectedOrganization] = useState<OrganizationOption | null>(null);
  const [organizationOptions, setOrganizationOptions] = useState<OrganizationOption[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [createNewOrganization, setCreateNewOrganization] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const query = organizationInput.trim();

    if (!query) {
      setOrganizationOptions([]);
      setCreateNewOrganization(false);
      return;
    }

    const timeoutId = window.setTimeout(async () => {
      try {
        setIsSearching(true);
        const res = await organizationsPublicApi.search({ query });
        const options = res.data?.data ?? [];
        const exactMatch = Boolean(res.data?.meta?.exact_match);
        setOrganizationOptions(options);
        setCreateNewOrganization(!exactMatch);
      } catch (error) {
        setOrganizationOptions([]);
        setCreateNewOrganization(false);
      } finally {
        setIsSearching(false);
      }
    }, 250);

    return () => window.clearTimeout(timeoutId);
  }, [organizationInput]);

  const canSubmit = useMemo(() => {
    return (
      name.trim() !== '' &&
      email.trim() !== '' &&
      phone.trim() !== '' &&
      organizationInput.trim() !== '' &&
      !isSubmitting
    );
  }, [name, email, phone, organizationInput, isSubmitting]);

  const shouldShowOrganizationAssist =
    organizationInput.trim() !== '' && (isSearching || organizationOptions.length > 0 || createNewOrganization);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;

    try {
      setIsSubmitting(true);

      const payload: {
        name: string;
        email: string;
        phone: string;
        organization_id?: number;
        organization_name?: string;
      } = {
        name: name.trim(),
        email: email.trim(),
        phone: phone.trim(),
      };

      if (selectedOrganization) {
        payload.organization_id = selectedOrganization.id;
      } else {
        payload.organization_name = organizationInput.trim();
      }

      await trialRegistrationsApi.create(payload);
      await trackEvent('trial_registration_submitted', {
        has_organization_id: Boolean(payload.organization_id),
        organization_id: payload.organization_id ?? null,
        has_organization_name: Boolean(payload.organization_name),
        organization_name_length: payload.organization_name ? payload.organization_name.length : null,
      }, {
        name: payload.name,
        email: payload.email,
        phone: payload.phone,
      });
      setSubmitted(true);
      toast.success('Registration received successfully.');
    } catch (error: any) {
      const apiMessage = error?.response?.data?.message as string | undefined;
      toast.error(apiMessage || 'Could not submit registration. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <main className="min-h-screen bg-slate-50 px-4 py-16">
        <div className="mx-auto w-full max-w-2xl rounded-2xl border border-slate-200 bg-white p-10 shadow-sm">
          <h1 className="text-3xl font-semibold text-slate-900">Thank you for registering</h1>
          <p className="mt-4 text-slate-600">
            Your 30-day ClickLaw free trial request has been received. Our team will contact you shortly.
          </p>
          <Link
            to="/"
            className="mt-8 inline-flex rounded-lg px-5 py-3 text-sm font-semibold text-white"
            style={{ backgroundColor: palette.primary }}
          >
            Back to Home
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-slate-50 px-4 py-10">
      <div className="mx-auto w-full max-w-3xl rounded-2xl border border-slate-200 bg-white p-8 shadow-sm md:p-10">
        <h1 className="text-3xl font-semibold text-slate-900">Start your 30-day free trial</h1>
        <p className="mt-3 text-slate-600">
          Register your interest and we will set up your trial.
        </p>

        <form className="mt-8 space-y-5" onSubmit={handleSubmit}>
          <div>
            <label className="mb-2 block text-sm font-medium text-slate-700" htmlFor="name">Full name</label>
            <input
              id="name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className="w-full rounded-lg border border-slate-300 px-3 py-2 outline-none focus:ring-2"
              style={{ borderColor: '#cbd5e1' }}
            />
          </div>

          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            <div>
              <label className="mb-2 block text-sm font-medium text-slate-700" htmlFor="email">Email</label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full rounded-lg border border-slate-300 px-3 py-2 outline-none"
              />
            </div>
            <div>
              <label className="mb-2 block text-sm font-medium text-slate-700" htmlFor="phone">Phone number</label>
              <input
                id="phone"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                required
                className="w-full rounded-lg border border-slate-300 px-3 py-2 outline-none"
              />
            </div>
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-slate-700" htmlFor="organization">Organization</label>
            <input
              id="organization"
              type="text"
              value={organizationInput}
              onChange={(e) => {
                setOrganizationInput(e.target.value);
                setSelectedOrganization(null);
              }}
              required
              autoComplete="off"
              className="w-full rounded-lg border border-slate-300 px-3 py-2 outline-none"
            />

            {shouldShowOrganizationAssist ? (
              <div className="mt-2 rounded-lg border border-slate-200 bg-slate-50 p-3">
                {isSearching ? (
                  <p className="text-sm text-slate-600">Searching organizations...</p>
                ) : organizationOptions.length > 0 ? (
                  <div className="space-y-2">
                    <p className="text-sm" style={{ color: '#0f172a' }}>Did you mean:</p>
                    <div className="flex flex-wrap gap-2">
                      {organizationOptions.map((option) => (
                        <button
                          type="button"
                          key={option.id}
                          className="rounded-full border px-3 py-1 text-sm hover:bg-slate-100"
                          style={{ borderColor: '#94a3b8', color: '#0f172a', backgroundColor: '#ffffff' }}
                          onClick={() => {
                            setSelectedOrganization(option);
                            setOrganizationInput(option.name);
                            setCreateNewOrganization(false);
                            void trackEvent('organization_selected', {
                              organization_id: option.id,
                              organization_name_length: option.name.length,
                            });
                          }}
                        >
                          {option.name}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : null}

                {createNewOrganization ? (
                  <p className="mt-2 text-sm" style={{ color: palette.primary }}>
                    Organization not found. It will be added.
                  </p>
                ) : null}
              </div>
            ) : null}
          </div>

          <button
            type="submit"
            disabled={!canSubmit}
            className="inline-flex rounded-lg px-5 py-3 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
            style={{ backgroundColor: palette.primary, color: '#ffffff' }}
          >
            {isSubmitting ? 'Submitting...' : 'Register for free trial'}
          </button>
        </form>
      </div>
    </main>
  );
};

export default TrialRegisterPage;
